import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { API, remapProductImageUrl } from '../lib/api';

const coerceNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};

const normalizeProduct = (product = {}) => ({
    ...product,
    price: coerceNumber(product.price, 0),
    compare_at_price: product.compare_at_price == null ? null : coerceNumber(product.compare_at_price, 0),
    images: Array.isArray(product.images) ? product.images.filter(Boolean).map(remapProductImageUrl) : [],
    main_image: remapProductImageUrl(product.main_image),
    stock_quantity: coerceNumber(product.stock_quantity, 0),
    shipping_cost: product.shipping_cost == null ? null : coerceNumber(product.shipping_cost, 0),
    grade_options: Array.isArray(product.grade_options) ? product.grade_options.filter(Boolean) : [],
    default_grade: product.default_grade || null,
});

const normalizeCategories = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string' && value.trim()) return [value.trim()];
    if (value && typeof value === 'object') {
        if (Array.isArray(value.categories)) return value.categories.filter(Boolean);
        if (Array.isArray(value.data)) return value.data.filter(Boolean);
    }
    return [];
};

const ShopPage = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                const [productsResult, categoriesResult] = await Promise.allSettled([
                    axios.get(`${API}/products`),
                    axios.get(`${API}/categories`)
                ]);

                if (!isMounted) return;

                if (productsResult.status === 'fulfilled') {
                    const rawProducts = Array.isArray(productsResult.value?.data)
                        ? productsResult.value.data
                        : [];
                    setProducts(rawProducts.map(normalizeProduct));
                } else {
                    console.error('Error fetching products:', productsResult.reason);
                    setProducts([]);
                }

                if (categoriesResult.status === 'fulfilled') {
                    setCategories(normalizeCategories(categoriesResult.value?.data));
                } else {
                    console.error('Error fetching categories:', categoriesResult.reason);
                    setCategories([]);
                }
            } catch (error) {
                console.error('Error fetching products:', error);
                if (isMounted) {
                    setProducts([]);
                    setCategories([]);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, []);

    const filteredProducts = selectedCategory === 'all' 
        ? products 
        : products.filter(p => p.category === selectedCategory);

    return (
        <div className="min-h-screen bg-white" data-testid="shop-page">
            <SEO
                title="Shop Pure Maple Syrup"
                description="Browse our collection of premium pure maple syrup products. From classic amber to dark robust, find the perfect grade for your kitchen. Free shipping on subscription orders."
                keywords="buy maple syrup, pure maple syrup, amber maple syrup, dark maple syrup, grade A, organic maple syrup, Troy PA"
                path="/shop"
            />
            <Header />
            
            <section className="pt-32 pb-16 border-b border-black/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <span className="text-maple-red font-mono text-sm tracking-widest">
                        PRODUCTS
                    </span>
                    <h1 className="font-anton text-5xl sm:text-6xl lg:text-7xl text-maple-dark mt-4">
                        SHOP
                    </h1>
                    <p className="text-maple-dark/60 text-lg mt-6 max-w-2xl">
                        Browse our selection of premium maple products, handcrafted in the hills of Pennsylvania.
                    </p>
                </div>
            </section>

            <section className="py-8 border-b border-black/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex flex-wrap gap-4">
                        <button
                            onClick={() => setSelectedCategory('all')}
                            className={`px-6 py-2 text-sm uppercase tracking-widest transition-all ${
                                selectedCategory === 'all'
                                    ? 'bg-maple-red text-white'
                                    : 'bg-black/5 text-maple-dark/60 hover:bg-black/10'
                            }`}
                            data-testid="filter-all"
                        >
                            All
                        </button>
                        {categories.map(category => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-6 py-2 text-sm uppercase tracking-widest transition-all ${
                                    selectedCategory === category
                                        ? 'bg-maple-red text-white'
                                        : 'bg-black/5 text-maple-dark/60 hover:bg-black/10'
                                }`}
                                data-testid={`filter-${category}`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                                <div key={i} className="bg-maple-card animate-pulse aspect-[4/5]" />
                            ))}
                        </div>
                    ) : filteredProducts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                            {filteredProducts.map(product => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 border border-black/10">
                            <p className="text-maple-dark/40 font-mono text-lg">No products found</p>
                            {selectedCategory !== 'all' && (
                                <button
                                    onClick={() => setSelectedCategory('all')}
                                    className="text-maple-red hover:underline mt-4"
                                >
                                    View all products
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ShopPage;
